import type { CrashCause } from './state.ts';

/**
 * What the simulation asks of the effects (GDD §3.7). The simulation stays free of three.js; src/effects/explosions.ts
 * implements this with the particle systems, the explosion light and camera shake, drawing from the effects stream.
 */
export interface Fx {
  /** An explosion of size `s` (0.7 turret … 2.2 bridge): fireball, sparks, smoke, splash near water, flash, shake. */
  boom(x: number, y: number, z: number, s: number): void;
  /** A helicopter hitting the water (concept splashSmall2). */
  splash(x: number, z: number, s: number): void;
  /** A bridge span hitting the water (concept splashAt). */
  spanSplash(x: number, z: number): void;
  /** A flak shell bursting: dark puff and orange flash. */
  flak(x: number, y: number, z: number): void;
  /** The flash of an enemy gun firing; `cause` says which gun ('heliFire', 'flak' or 'bridgeGuns'). */
  muzzle(x: number, y: number, z: number, vx: number, vz: number, cause: CrashCause): void;
  /** Sparks where a tracer strikes the bridge. */
  bridgeHit(x: number, y: number, z: number): void;
  /** Smoke from a sinking boat or barge: a chance each step. */
  sinkSmoke(x: number, z: number, dt: number): void;
  /** Smoke over a broken bridge for its first 4 s: a chance each step. */
  bridgeSmoke(x: number, y: number, z: number, dt: number): void;
  /** Start of a step: the explosion light fades. */
  step(dt: number): void;
}

/** The effects the audio hears (GDD §9): src/game/game.ts passes them on to `game.hooks.sound`. */
export type SoundTap = Pick<Fx, 'boom' | 'splash' | 'spanSplash' | 'flak' | 'muzzle' | 'bridgeHit'>;

/** Effects that draw nothing and consume no randomness (rule tests that never compare effects). */
export const NO_FX: Fx = {
  boom() {},
  splash() {},
  spanSplash() {},
  flak() {},
  muzzle() {},
  bridgeHit() {},
  sinkSmoke() {},
  bridgeSmoke() {},
  step() {},
};
